import { useEffect } from 'react'

const TYPED_INPUT_TYPES = new Set(['text', 'search', 'email', 'password', 'number', 'tel', 'url'])

function shouldIgnoreEnterForTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    if (target.isContentEditable) return true
    const tag = target.tagName
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (tag === 'INPUT') {
        const type = (target as HTMLInputElement).type?.toLowerCase() ?? 'text'
        if (TYPED_INPUT_TYPES.has(type)) return true
    }
    return false
}

type Options = {
    onSubmit?: () => void | Promise<void>
    onClose?: () => void
    submitDisabled?: boolean
}

/** Document-level Enter/Escape for non-modal confirmation dialogs (focus often stays outside the paper). */
export function useConfirmationDialogKeyboard(open: boolean, options: Options) {
    const { onSubmit, onClose, submitDisabled } = options

    useEffect(() => {
        if (!open) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose?.()
                return
            }
            if (event.key !== 'Enter' || event.repeat) return
            if (!onSubmit || submitDisabled) return
            if (shouldIgnoreEnterForTarget(event.target)) return
            event.preventDefault()
            event.stopPropagation()
            void Promise.resolve(onSubmit())
        }

        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [open, onSubmit, onClose, submitDisabled])
}
